import React, { useState, useRef, useEffect, useCallback } from 'react';
import { authAxios } from '../../services/crmApi';

interface Props {
  value: string;
  onChange: (val: string) => void;
  rows?: number;
  placeholder?: string;
  className?: string;
}

interface UserOption { id: number; username: string; first_name: string; last_name: string; }

export function MentionsInput({ value, onChange, rows = 6, placeholder, className = '' }: Props) {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionStart = useRef(-1);

  useEffect(() => {
    authAxios.get('/api/auth/operators')
      .then(r => {
        const ops = (r.data?.operators || r.data || []).map((o: any) => ({
          id: o.id,
          username: o.username,
          first_name: o.first_name || o.name?.split(' ')[0] || '',
          last_name: o.last_name || o.name?.split(' ').slice(1).join(' ') || '',
        }));
        setUsers(ops);
      })
      .catch(() => {});
  }, []);

  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(text);

    // Check if user just typed @
    const lastAt = text.lastIndexOf('@', cursorPos - 1);
    if (lastAt >= 0) {
      const textAfterAt = text.slice(lastAt + 1, cursorPos);
      // Only show if @ is at start or preceded by space
      const charBefore = lastAt > 0 ? text[lastAt - 1] : ' ';
      if ((charBefore === ' ' || charBefore === '\n' || lastAt === 0) && !textAfterAt.includes(' ')) {
        mentionStart.current = lastAt;
        setFilter(textAfterAt.toLowerCase());
        setShowDropdown(true);
        // Position dropdown
        if (textareaRef.current) {
          const rect = textareaRef.current.getBoundingClientRect();
          setDropdownPos({ top: rect.height + 4, left: 0 });
        }
        return;
      }
    }
    setShowDropdown(false);
  }, [onChange]);

  const insertMention = (user: UserOption) => {
    const before = value.slice(0, mentionStart.current);
    const after = value.slice(textareaRef.current?.selectionStart || mentionStart.current);
    const mention = `@${user.first_name}_${user.last_name} `;
    onChange(before + mention + after);
    setShowDropdown(false);

    // Notify mentioned user
    authAxios.post('/api/operator/mention', {
      mentioned_user_id: user.id,
      context: value.slice(0, 50),
    }).catch(() => {});

    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const filtered = users.filter(u =>
    `${u.first_name} ${u.last_name} ${u.username}`.toLowerCase().includes(filter)
  ).slice(0, 6);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        rows={rows}
        placeholder={placeholder}
        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${className}`}
        onKeyDown={e => {
          if (showDropdown && e.key === 'Escape') { setShowDropdown(false); e.preventDefault(); }
          if (showDropdown && e.key === 'Enter' && filtered.length > 0) { insertMention(filtered[0]); e.preventDefault(); }
        }}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
      />
      {showDropdown && filtered.length > 0 && (
        <div
          className="absolute z-50 bg-white border rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto w-56"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          {filtered.map(u => (
            <button
              key={u.id}
              type="button"
              onMouseDown={e => { e.preventDefault(); insertMention(u); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 flex items-center gap-2"
            >
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold">
                {u.first_name?.[0]}{u.last_name?.[0]}
              </div>
              <span>{u.first_name} {u.last_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
